import { z } from 'zod';
import { objectId, timestampFields, FieldMetadata } from './base';
import { RegistrationFieldSchema } from './registration';
import { AcquisitionSchema } from './admin';

/**
 * Organization Schema
 * Represents an organization (formerly called League)
 */

// Steps taken tracking sub-schema
export const StepsTakenSchema = z.object({
  createdStripeAccount: z.boolean().optional(),
  createdDivision: z.boolean().optional(),
  // Set when the admin builds their first real structure via the Get Started page
  // (distinct from the default sample structure auto-seeded at org creation).
  builtFirstStructure: z.boolean().optional(),
  // Review-checklist flags (ADR 0038): stamped server-side on the admin's first
  // REAL edit of a competition / registration (update controllers), or by the
  // Get Started "Looks good" endpoint (POST /organizations/:id/steps). Never
  // stamped by bootstrap or onboardingBuild defaults — these are the
  // deliberate-action signals now that every new org bootstraps a working
  // structure + registration.
  customizedCompetition: z.boolean().optional(),
  reviewedRegistration: z.boolean().optional(),
});

export type StepsTaken = z.infer<typeof StepsTakenSchema>;

// Organizer funnel markers (INT-0028): platform-written timestamps of the
// organizer's own activation actions. Written ONLY via atomic dotted $set
// (apps/api/api/utils/orgFunnelMarkers.ts) and stripped from the generic org
// update path. Never default to {} — absent means "never happened".
export const FunnelMarkersSchema = z.object({
  // First time the organizer copied/shared/emailed their registration link
  // (wizard finish screen or Get Started). Set once, never overwritten.
  linkSharedAt: z.coerce.date().optional(),
  // First REAL team on the org (admin Add Team, import, public registration;
  // never the wizard/bootstrap example teams) and first game scheduled
  // (any create path). INT-0042. Set once, never overwritten.
  firstTeamAt: z.coerce.date().optional(),
  firstGameAt: z.coerce.date().optional(),
});

export type FunnelMarkers = z.infer<typeof FunnelMarkersSchema>;

// Banner settings sub-schema
export const BannerSettingsSchema = z.object({
  bannerType: z.enum(['custom', 'upload']).optional(),
  colorPaletteKey: z.string().optional(),
  bannerTemplateKey: z.string().optional(),
  fontKey: z.string().optional(),
  // INT-0057: the organizer's own pair, used when colorPaletteKey is
  // 'custom' (CUSTOM_PALETTE_KEY in orgAppearance.ts). Kept when a curated
  // palette is re-selected so the pair round-trips. Normalized #RRGGBB.
  customColors: z.object({ primary: z.string(), secondary: z.string() }).optional(),
  // Where the org logo sits INSIDE a custom template banner (owner
  // 2026-09-09: on the simple template the logo is part of the banner; the
  // header logo yields to it). NO .default() — absent resolves to 'left'
  // at render time (config-default rules).
  logoPosition: z.enum(['left', 'right']).optional(),
});

export type BannerSettings = z.infer<typeof BannerSettingsSchema>;

// Organization settings sub-schema
export const OrganizationSettingsSchema = z.object({
  banner: BannerSettingsSchema.optional(),
  // Public org-site homepage template (INT-0006/0008). Absent = 'simple'
  // (owner 2026-09-08 — the ORIGINAL pre-template layout, so orgs that
  // predate the feature keep their look untouched); 'classic' = calm,
  // logo-led; 'premier' = full-bleed ink hero + saturated palette. NO
  // .default() — absent must mean "simple by fallback", per the
  // config-hierarchy default rules.
  homeTemplate: z.enum(['classic', 'premier', 'simple']).optional(),
  // Hero logo emphasis for classic/premier (owner 2026-09-09): 'prominent'
  // renders a BIG logo beside the org name and pushes the hero's right rail
  // (classic value props / premier featured card) to a horizontal bottom
  // row. NO .default() — absent = 'standard' (today's look) at resolution.
  heroLogo: z.enum(['standard', 'prominent']).optional(),
  // ONE focal point for the custom premier hero photo (INT-0013): percent
  // coordinates of the image's important spot — drives background-position
  // at every breakpoint. Optional; absent = center (50/50).
  heroFocal: z.object({ x: z.number().min(0).max(100), y: z.number().min(0).max(100) }).optional(),
  // INT-0009 template-aware appearance. Per-template storage so switching
  // templates round-trips choices; ALL optional with NO defaults — absent
  // reproduces the pre-INT-0009 rendering exactly (premier: gradient-only
  // hero; classic: clean white band; fonts: the template's fixed default).
  // Keys resolve through packages/shared/orgAppearance.ts.
  heroPremier: z.string().optional(),
  // Keys must track CLASSIC_HERO_TREATMENTS (glow/dots/grid added
  // 2026-09-08; enum lagged until 2026-09-09 — saving those keys failed
  // validation silently).
  heroClassic: z.enum(['clean', 'tint', 'lines', 'glow', 'dots', 'grid']).optional(),
  fonts: z
    .object({
      premier: z.string().optional(),
      classic: z.string().optional(),
      // INT-0056: site BODY font, one key for every template (BODY_FONTS in
      // orgAppearance.ts). Absent = Inter, today's exact look.
      body: z.string().optional(),
    })
    .optional(),
  // Fields the admin requires every signed-in user of this org to fill out
  // before they can begin a registration on apps/app.
  profileFields: z.array(RegistrationFieldSchema).default([]),
  // Deprecated. Picking DUPR on the admin's Rating-source radio now adds
  // the canonical DUPR_FIELD directly to `profileFields` (see
  // `packages/shared/utils/profileFieldCatalog.ts`). Field is kept on the
  // schema so legacy organization records that still have it set load
  // without validation errors, and a one-release backwards-compat shim in
  // apps/app's OrgProfileForm / ProfileEditModal still injects the DUPR
  // ID input for those legacy orgs. Remove the field + shim once stale
  // orgs have re-saved their settings.
  duprEnabled: z.boolean().optional(),
});

export type OrganizationSettings = z.infer<typeof OrganizationSettingsSchema>;

export const OrganizationSchema = z.object({
  _id: objectId,
  name: z.string().default(''),
  // INT-0040: every slug this org used before (set-only, never default).
  // Old subdomains 301 to the current one and are never claimable by
  // another org. Platform-written via PUT /organizations/:id/slug only.
  previousSlugs: z.array(z.string()).optional(),
  slugChangedAt: z.coerce.date().optional(),
  displayName: z.string().optional(),
  fullName: z.string().optional(),
  sports: z.array(z.string()).default(['basketball']),
  url: z.string().optional(),
  about: z.string().optional(),
  location: z.string().default(''),
  emails: z.array(z.string()).default([]),
  hasRegistration: z.boolean().optional(),
  organizationId: z.string().optional(),
  // hideStats RETIRED (ADR 0035 follow-on) — stats visibility lives in the
  // config hierarchy (config.showStats); script 25 migrated affected orgs.
  // Existing documents keep the field as a fossil; strict hydration hides it.
  hidden: z.boolean().optional(),
  hasBanner: z.boolean().optional(),
  bannerExt: z.string().default(''),
  // Org logo (INT-0006) — uploaded via /organizations/d/:name/logoupload2,
  // stored at leagues/<name>/leaguelogo.<ext> on Spaces, mirroring the banner.
  hasLogo: z.boolean().optional(),
  logoExt: z.string().default(''),
  // Custom premier hero photo (INT-0013) — uploaded via
  // /organizations/d/:name/heroupload2, stored at
  // leagues/<name>/heropremier.<ext>; selected via settings.heroPremier
  // 'custom'; cropped around settings.heroFocal.
  hasHeroImage: z.boolean().optional(),
  heroImageExt: z.string().default(''),
  // INT-0014 responsive variants (pre-generated — Spaces has no on-the-fly
  // resize): leaguelogo-sm.webp (256w) / heropremier-mobile.webp (768w).
  // Stamped by the upload/select post-process + backfill script 42.
  hasLogoSmall: z.boolean().optional(),
  hasHeroImageMobile: z.boolean().optional(),
  stepsTaken: StepsTakenSchema.optional(),
  settings: OrganizationSettingsSchema.optional(),
  // IANA timezone (e.g. "America/Los_Angeles"), stamped at org creation from
  // the browser (ADR 0052). Optional — consumers fall back to the newest
  // registration's timeZone, then UTC (resolveOrgTimeZone in the api).
  timeZone: z.string().optional(),
  stripeAccountId: z.string().optional(), // Note: select: false in Mongoose
  // Staty platform application fee (% of each registration payment, Stripe
  // Connect application_fee_amount). ABSENT means the standard rate (1% + 30¢ fixed) — resolve
  // via getPlatformFeePercent(); explicit 0 = grandfathered/exempt (script 29).
  // Platform-controlled: stripped from the public update endpoint. No default
  // here — stamping 1 onto docs would freeze future standard-rate changes.
  platformFeePercent: z.number().optional(),
  // Owner funnel-milestone email markers ("New Staty Org …" alerts): milestone
  // key → Date sent. Written only via atomic dotted $set in
  // apps/api/api/utils/orgFunnelAlerts.ts; the marker doubles as the
  // once-per-org dedup. Never default to {}.
  funnelAlerts: z.record(z.coerce.date()).optional(),
  // Organizer activation markers (INT-0028) — see FunnelMarkersSchema.
  funnelMarkers: FunnelMarkersSchema.optional(),
  // The organizer's season / tournament start date, captured ONCE from the
  // create wizard's first-game date (INT-0043) and read by the date-driven
  // nudges (INT-0046). Platform-written via onboarding/teams only (stripped
  // from the generic update); no default — absent means "never told us".
  seasonStartsAt: z.coerce.date().optional(),
  // Snapshot of the creating admin's first-touch acquisition attribution
  // (funnel analytics 2026-09-14). Copied at org create — organization.admin
  // is dropped by the strict schema, so a read-time join is unreliable.
  acquisition: AcquisitionSchema.optional(),
  // Custom domain for the org's tenant site (INT-0007, free for every org).
  // One domain per org, exact hostname. Provisioned as a Cloudflare-for-SaaS
  // custom hostname on the statyapps.io zone; written ONLY by the dedicated
  // customDomain endpoints (stripped from the generic update paths — an
  // arbitrary write would bypass CF provisioning + cross-org uniqueness).
  // status lifecycle: pending_dns → pending_ssl → active; failed on cert/DNS
  // errors (subdomain serving keeps working throughout). `verifiedAt` has NO
  // default on purpose (toMongooseSchema would auto-stamp Date.now).
  customDomain: z
    .object({
      hostname: z.string(),
      status: z.enum(['pending_dns', 'pending_ssl', 'active', 'failed']),
      cfHostnameId: z.string().optional(), // select: false in Mongoose
      verifiedAt: z.coerce.date().optional(),
    })
    .optional(),
  ...timestampFields,
});

export type Organization = z.infer<typeof OrganizationSchema>;

// Metadata for Organization fields (for Mongoose refs and UI)
export const organizationMetadata: Record<string, FieldMetadata> = {
  _id: { hidden: true },
  name: { label: 'Organization Name', editable: true },
  displayName: { label: 'Display Name', editable: true },
  fullName: { label: 'Full Name', editable: true },
  sports: { label: 'Sports', editable: true },
  url: { label: 'URL', editable: true },
  about: { label: 'About', editable: true },
  location: { label: 'Location', editable: true },
  emails: { label: 'Emails', editable: true },
  hasRegistration: { label: 'Has Registration', editable: true },
  organizationId: { label: 'Organization ID', editable: true },
  hidden: { label: 'Hidden', editable: true },
  hasBanner: { label: 'Has Banner', editable: true },
  bannerExt: { label: 'Banner Extension', editable: true },
  hasLogo: { label: 'Has Logo', editable: true },
  logoExt: { label: 'Logo Extension', editable: true },
  stepsTaken: { label: 'Steps Taken', hidden: true },
  settings: { label: 'Settings', editable: true },
  stripeAccountId: { hidden: true },
  platformFeePercent: { label: 'Platform Fee %', hidden: true },
  timeZone: { label: 'Time Zone', hidden: true },
  funnelAlerts: { label: 'Funnel Alerts', hidden: true },
  funnelMarkers: { label: 'Funnel Markers', hidden: true },
  seasonStartsAt: { label: 'Season Starts At', hidden: true },
  previousSlugs: { label: 'Previous Slugs', hidden: true },
  slugChangedAt: { label: 'Slug Changed At', hidden: true },
  acquisition: { label: 'Acquisition', hidden: true },
  customDomain: { label: 'Custom Domain', hidden: true },
  created: { hidden: true },
  updated: { hidden: true },
};
